/**
 * Turn a requirement's stored job description into a public job posting. Pure — exported for tests.
 *
 * The stored text is recruiter Markdown plus the lines the New requirement page appends
 * ("### Department: Engineering", "### Budget Range: …"). Public page needs:
 *  - those `### Label: Value` lines as label/value pairs (Job details), not raw `###` text;
 *  - plain section lines ("Role & Responsibilities") as headings, and the lines under a list-type
 *    section (responsibilities, requirements…) as bullets, so a pasted JD reads as a structured list;
 *  - nothing internal: interview languages and the screening-skills list (the Skills chips show those).
 */

export interface JobDetail {
  label: string;
  value: string;
}

export interface JobPosting {
  /** Markdown for MarkdownView: ### headings, - bullets, paragraphs. */
  body: string;
  details: JobDetail[];
}

/** Lines the app adds that aren't for candidates. */
const HIDDEN_DETAIL = /^(interview languages?|required screening skills|screening skills|skills)$/i;

/** A plain line that is a section heading, e.g. "Role & Responsibilities", "Requirements:". */
const SECTION_RE =
  /^(?:(?:key\s+)?roles?\s*(?:&|and|\/)?\s*responsibilit\w*|responsibilit\w*|(?:job|role)\s+(?:description|summary|overview|purpose)|about\s+(?:the\s+)?(?:role|job|company|us|team)|(?:preferred|ideal|desired)\s+candidate(?:\s+profile)?|candidate\s+profile|requirements?|qualifications?|(?:key\s+|required\s+|must[-\s]have\s+)?skills?(?:\s*(?:&|and)\s*\w+)?|what\s+you(?:'ll|\s+will)\s+do|who\s+you\s+are|what\s+we\s+offer|benefits|perks|nice\s+to\s+have|good\s+to\s+have|education|duties|desired\s+profile)\s*:?$/i;

/** Sections whose lines are a list of items, so un-bulleted lines become bullets. */
const LIST_SECTION_RE = /responsibilit|requirement|qualification|duties|what you('ll| will) do|nice to have|good to have|must[- ]have|benefits|perks|what we offer|who you are/i;

const BULLET_RE = /^\s*(?:[-*•▪◦]|\d+[.)])\s+/;

const title = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export function parseJobDescription(text: string, jobTitle?: string): JobPosting {
  const details: JobDetail[] = [];
  const out: string[] = [];
  let inList = false;
  let skipping = false; // inside a hidden section (screening skills)
  let first = true;

  for (const raw of text.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trim();

    // A first line that only repeats the job title — the page header already shows it.
    if (first && line) {
      first = false;
      if (jobTitle && line.replace(/^#+\s*/, "").toLowerCase() === jobTitle.trim().toLowerCase()) continue;
    }

    if (!line) {
      out.push("");
      continue;
    }

    // "### Label: Value" → a job detail. "### Label:" with nothing after is a section heading.
    const detail = line.match(/^#{1,6}\s*([^:#]{2,40}):\s*(\S.*)$/);
    if (detail) {
      const label = (detail[1] ?? "").trim();
      skipping = false;
      inList = false;
      if (!HIDDEN_DETAIL.test(label)) details.push({ label: title(label), value: (detail[2] ?? "").trim() });
      continue;
    }

    const heading = line.match(/^#{1,6}\s*(.+?)\s*:?\s*$/);
    const plainHeading = !heading && !BULLET_RE.test(line) && line.length <= 60 && SECTION_RE.test(line);
    if (heading || plainHeading) {
      const text = (heading ? (heading[1] ?? "") : line.replace(/:\s*$/, "")).trim();
      skipping = HIDDEN_DETAIL.test(text);
      inList = LIST_SECTION_RE.test(text);
      if (!skipping) out.push(`### ${title(text)}`);
      continue;
    }

    if (skipping) continue;

    if (BULLET_RE.test(line)) {
      out.push(`- ${line.replace(BULLET_RE, "")}`);
    } else if (inList) {
      // "Configure and manage finance processes, including:" stays a bullet; its "- …" items follow it.
      out.push(`- ${line}`);
    } else {
      out.push(line);
    }
  }

  const body = out
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return { body, details };
}
